import { useEffect, useMemo, useState, useRef } from 'react';
import EmployeeService from '../services/EmployeeService';

export interface HeaderData {
  id: number;
  fullName: string;
  email: string | null;
  phone: string | null;
  birthDate: string | null;
  startDate: string | null;
  employmentType: string | null;
  positionTitle: string | null;
  photoUrl: string | null;
  workAreas: { id: number; name: string; is_primary?: boolean }[];
  manager?: { first_name: string; last_name: string; email?: string; phone?: string } | null;
  onboardingStatus: string;
  onboardingMilestones: any[];
  scheduledDeactivationDate: string | null;
}

type FetchState<T> = { data?: T; loading: boolean; error?: unknown };

export function useEmployeeDetailData(employeeId: number, options?: { skipProfile?: boolean }) {
  const skipProfile = options?.skipProfile ?? false;
  const [overview, setOverview] = useState<FetchState<any>>({ loading: true });
  const [profile, setProfile] = useState<FetchState<any>>({ loading: true });
  const [refreshCounter, setRefreshCounter] = useState(0);
  const mounted = useRef(true);
  const fetchedRef = useRef<number | null>(null);
  const fetchedSkipProfileRef = useRef<boolean | null>(null);

  const refetch = () => {
    fetchedRef.current = null;
    fetchedSkipProfileRef.current = null;
    setRefreshCounter((c) => c + 1);
  };

  const refetchProfile = async () => {
    setProfile((s) => ({ ...s, loading: true }));
    try {
      const data = await EmployeeService.getEmployeeProfile(employeeId);
      if (!mounted.current) return;
      setProfile({ data, loading: false });
    } catch (e) {
      if (!mounted.current) return;
      setProfile({ data: null, loading: false, error: e });
    }
  };

  const refetchOverview = async () => {
    setOverview((s) => ({ ...s, loading: true }));
    try {
      const data = await EmployeeService.getEmployeeOverview(employeeId);
      if (!mounted.current) return;
      setOverview({ data, loading: false });
    } catch (e) {
      if (!mounted.current) return;
      setOverview({ data: null, loading: false, error: e });
    }
  };

  useEffect(() => {
    mounted.current = true;

    if (fetchedRef.current === employeeId && fetchedSkipProfileRef.current === skipProfile && refreshCounter === 0) {
      return;
    }

    fetchedRef.current = employeeId;
    fetchedSkipProfileRef.current = skipProfile;
    setOverview({ loading: true });

    if (skipProfile) {
      setProfile({ data: null, loading: false });
    } else {
      setProfile({ loading: true });
    }

    (async () => {
      const promises: [Promise<any>, Promise<any> | null] = [
        EmployeeService.getEmployeeOverview(employeeId),
        skipProfile ? null : EmployeeService.getEmployeeProfile(employeeId),
      ];

      const [ovRes, prRes] = await Promise.allSettled(
        promises.filter(Boolean) as Promise<any>[]
      );

      if (!mounted.current) return;

      if (ovRes.status === 'fulfilled') {
        setOverview({ data: ovRes.value, loading: false });
      } else {
        setOverview({ data: null, loading: false, error: ovRes.reason });
      }

      if (!skipProfile && prRes) {
        if (prRes.status === 'fulfilled') {
          setProfile({ data: prRes.value, loading: false });
        } else {
          setProfile({ data: null, loading: false, error: prRes.reason });
        }
      }
    })();

    return () => {
      mounted.current = false;
    };
  }, [employeeId, refreshCounter, skipProfile]);

  const header: HeaderData | null = useMemo(() => {
    if (!overview.data && !profile.data) {
      return null;
    }

    const ov = overview.data || {};
    const pr = profile.data || {};

    const empOv = ov.employee || ov;
    const person = pr.person || pr;
    const job = pr.job || pr;

    const firstName = person.first_name ?? empOv.first_name ?? '';
    const lastName = person.last_name ?? empOv.last_name ?? '';
    const email = person.email ?? empOv.email ?? null;
    const phone = person.phone_number ?? empOv.phone_number ?? empOv.phone ?? null;
    const photoUrl = person.photo_url ?? empOv.photo_url ?? null;

    const birthDate = person.birth_date ?? empOv.birth_date ?? null;
    const startDate = job.start_date ?? job.employment_start_date ?? empOv.start_date ?? null;
    const employmentType = job.employment_type ?? empOv.employment_type ?? ov.key_facts?.employment_type ?? null;
    const positionTitle = job.position_title ?? empOv.position_title ?? ov.key_facts?.position_title ?? null;

    const workAreas = Array.isArray(ov.work_areas) ? ov.work_areas : [];

    const managers = ov.managers;
    const primaryWA = ov.primary_work_area;

    let manager = managers?.primary || ov.primary_work_area_manager || ov.manager_primary_wa || null;

    if (!manager && managers?.by_work_area && primaryWA?.id) {
      const waEntry = managers.by_work_area.find((x: any) => x.work_area_id === primaryWA.id);
      manager = waEntry?.managers?.[0] || null;
    }

    const validStatuses = ['INVITED', 'WAITING_FOR_MANAGER', 'ACTIVE', 'DEACTIVATED', 'PENDING_DEACTIVATION'];
    const rawStatus = empOv.onboarding_status ?? ov.onboarding_status;
    const onboardingStatus = rawStatus && validStatuses.includes(rawStatus) ? rawStatus : 'ACTIVE';
    const onboardingMilestones = ov.onboarding_milestones ?? ov?.onboarding?.steps ?? [];
    const scheduledDeactivationDate = empOv.scheduled_deactivation_date ?? null;

    return {
      id: employeeId,
      fullName: [firstName, lastName].filter(Boolean).join(' ') || '—',
      email,
      phone,
      birthDate,
      startDate,
      employmentType,
      positionTitle,
      photoUrl,
      workAreas,
      primaryWorkArea: primaryWA,
      manager,
      onboardingStatus,
      onboardingMilestones,
      scheduledDeactivationDate,
    };
  }, [employeeId, overview.data, profile.data]);

  const isLoading = overview.loading && profile.loading;

  return {
    overview,
    profile,
    header,
    loading: isLoading,
    error: overview.error || profile.error,
    refetch,
    refetchProfile,
    refetchOverview,
  };
}
